import React from "react";
import { Box, Paper } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { createRecipeStyles } from "./styles";
import { getStaticAssetPath } from "../../utils/staticAssets";
import { FetchedRecipe, Author, Contributor } from "../../types/recipe";
import RecipeDetails from "./RecipeDetails";

/**
 * Props for RecipeImageAndDetails component.
 * @property {FetchedRecipe} recipe - The recipe object to display.
 * @property {Author | null} author - The author of the recipe.
 * @property {Contributor[]} fullContributors - List of all contributors.
 */
interface RecipeImageAndDetailsProps {
  recipe: FetchedRecipe;
  author: Author | null;
  fullContributors: Contributor[];
}

/**
 * RecipeImageAndDetails Component
 *
 * Displays the recipe's main image and details side by side in a responsive grid.
 * The left column shows the cover image (or a fallback), and the right column
 * shows recipe details including author and contributors.
 *
 * Features:
 * - Responsive grid layout for image and details
 * - Fallback image if no cover image is provided
 * - Delegates details rendering to RecipeDetails component
 *
 * @param {RecipeImageAndDetailsProps} props - Component props
 * @returns {JSX.Element} The rendered image and details section
 */
const RecipeImageAndDetails: React.FC<RecipeImageAndDetailsProps> = ({
  recipe,
  author,
  fullContributors,
}) => {
  const theme = useTheme();
  const styles = createRecipeStyles(theme);

  // Minimum height for hero image per breakpoint
  const HERO_MIN_HEIGHT = {
    md: 260,
    lg: 340,
    xl: 380,
  } as const;

  // Get image source or fallback
  const imgSrc =
    recipe.data &&
    recipe.data.coverImage &&
    typeof recipe.data.coverImage.source === "string" &&
    recipe.data.coverImage.source.length > 0
      ? getStaticAssetPath(recipe.data.coverImage.source)
      : null;

  return (
    <Paper elevation={0} sx={styles.page.imageAndDetailsPaper}>
      <Box sx={styles.page.imageAndDetailsGrid}>
        {/* Image column using background image */}
        <Box
          sx={{
            ...styles.page.imageColumn,
            backgroundImage: `url(${
              imgSrc ||
              "https://materiom.cottagelabs.com/static/img/recipe_shapes/recipe_shape_2.png"
            })`,
          }}
        />

        {/* Details column */}
        <Box sx={styles.page.detailsColumn}>
          {/* Render recipe details */}
          <RecipeDetails
            recipe={recipe}
            author={author}
            fullContributors={fullContributors}
          />
        </Box>
      </Box>
    </Paper>
  );
};

export default RecipeImageAndDetails;
